import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const LOAN_PAGE_PATH = '/dashboard/apply-loan';

const fetchWithTimeout = async (url, options, ms = 20000) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    try {
        return await fetch(url, { ...options, signal: controller.signal });
    } catch (err) {
        if (err.name === 'AbortError') throw new Error('Server took too long to respond.');
        throw err;
    } finally {
        clearTimeout(timer);
    }
};

function DigilockerClosePage() {
    const location = useLocation();
    const navigate = useNavigate();
    const [status, setStatus] = useState('Finalizing Digilocker consent...');
    const [error, setError] = useState('');
    const [requestId, setRequestId] = useState('');

    useEffect(() => {
        let cancelled = false;

        const returnToLoan = () => {
            setTimeout(() => {
                if (window.opener && window.opener !== window) {
                    window.close();
                }
                // Popup couldn't close (no opener / same-tab flow): go back to the loan page.
                setTimeout(() => navigate(LOAN_PAGE_PATH, { replace: true }), 300);
            }, 800);
        };

        const finalizeKyc = async () => {
            const params = new URLSearchParams(location.search);
            const requestIdFromQuery = params.get('requestId') || params.get('kid') || params.get('request_id');
            const queryConsentId = params.get('consentId') || params.get('consent_id');
            const finalized = params.get('finalized') === '1';
            const session = JSON.parse(localStorage.getItem('userSession') || '{}');
            const token = session?.token || '';
            const userId = session?.userId;
            const apiUrl = process.env.REACT_APP_API_URL || '';
            let requestId = requestIdFromQuery;

            try {
                if (!requestId) {
                    setStatus('Loading Digilocker request...');
                    const statusResponse = await fetchWithTimeout(`${apiUrl}/api/digilocker/status`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {})
                        },
                        body: JSON.stringify({ userId })
                    });
                    const statusJson = await statusResponse.json().catch(() => ({}));
                    const record = statusJson?.record || {};
                    requestId = record.request_id || record.requestId || '';
                    if (!requestId) {
                        throw new Error('Missing Digilocker request id.');
                    }
                }
                if (!cancelled) {
                    setRequestId(requestId);
                }

                if (finalized) {
                    if (!cancelled) {
                        setStatus('Aadhaar fetched successfully. Returning...');
                        returnToLoan();
                    }
                    return;
                }

                setStatus(`Checking Digilocker consent for ${requestId}...`);
                const statusResponse = await fetchWithTimeout(`${apiUrl}/api/digilocker/status`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    },
                    body: JSON.stringify({ requestId, userId })
                });
                const statusJson = await statusResponse.json().catch(() => ({}));
                const record = statusJson?.record || {};
                const consentId = queryConsentId || record.consent_id || record.consentId || record?.callback_payload?.consentId || null;

                setStatus(`Fetching Aadhaar details for ${requestId}...`);
                const fetchResponse = await fetchWithTimeout(`${apiUrl}/api/digilocker/eaadhaar`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    },
                    body: JSON.stringify({
                        requestId,
                        consentId,
                        userId,
                        callbackPayload: record?.callback_payload || null
                    })
                });
                const fetchJson = await fetchResponse.json().catch(() => ({}));
                if (!fetchResponse.ok || !fetchJson.success) {
                    throw new Error(fetchJson.message || 'Unable to fetch Aadhaar details');
                }

                if (!cancelled) {
                    setStatus('Aadhaar fetched successfully. Returning...');
                    returnToLoan();
                }
            } catch (err) {
                if (cancelled) return;
                // The webhook or the backend close handler may already have saved the
                // Aadhaar data — if so, don't show an error, just go back.
                if (requestId) {
                    try {
                        const res = await fetchWithTimeout(`${apiUrl}/api/digilocker/status`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                ...(token ? { Authorization: `Bearer ${token}` } : {})
                            },
                            body: JSON.stringify({ requestId, userId })
                        }, 10000);
                        const json = await res.json().catch(() => ({}));
                        if (json?.record?.aadhaar_uid && !cancelled) {
                            setStatus('Aadhaar fetched successfully. Returning...');
                            returnToLoan();
                            return;
                        }
                    } catch (_) { /* fall through to the error state */ }
                }
                if (cancelled) return;
                setError(err.message || 'Failed to finalize Digilocker.');
                setStatus('Digilocker finalization failed. Returning to your loan application...');
                setTimeout(() => navigate(LOAN_PAGE_PATH, { replace: true }), 4000);
            }
        };

        finalizeKyc();

        return () => {
            cancelled = true;
        };
    }, [location.search, navigate]);

    return (
        <div style={{
            minHeight: '100vh',
            display: 'grid',
            placeItems: 'center',
            background: '#faf6ec',
            color: '#1e293b',
            padding: '2rem',
            textAlign: 'center'
        }}>
            <div style={{
                background: 'white',
                border: '1px solid #e9d9ae',
                borderRadius: '18px',
                padding: '2rem',
                boxShadow: '0 10px 30px rgba(139, 105, 20,0.08)',
                maxWidth: '420px'
            }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🏦</div>
                <h1 style={{ margin: 0, fontSize: '1.3rem' }}>Digilocker completed</h1>
                {requestId && (
                    <p style={{
                        margin: '0.5rem 0 0',
                        fontSize: '0.78rem',
                        color: '#a5761e',
                        fontWeight: 700,
                        wordBreak: 'break-all'
                    }}>
                        Request ID: {requestId}
                    </p>
                )}
                <p style={{ margin: '0.75rem 0 0', color: '#64748b' }}>{status}</p>
                {error && (
                    <p style={{ margin: '0.75rem 0 0', color: '#dc2626' }}>{error}</p>
                )}
                {error && (
                    <button
                        type="button"
                        onClick={() => navigate(LOAN_PAGE_PATH, { replace: true })}
                        style={{ marginTop: '1rem', padding: '0.6rem 1.2rem', borderRadius: '10px', border: 'none', background: '#a5761e', color: 'white', fontWeight: 600, cursor: 'pointer' }}
                    >
                        Back to loan application
                    </button>
                )}
            </div>
        </div>
    );
}

export default DigilockerClosePage;
